/**
 * Spec 196 (CC-54309) — the status cb-api actually answered.
 *
 * Cloudflare replaces a 502 or 504 answered by the Report Center's /api/v1 Pages
 * Function with its own "Error 502: Bad gateway" page, so that Function relays those
 * two on 503 with cb-api's body intact and the real status in this header
 * (functions/api/v1/[[path]].js — keep the name in step; a test pins the two).
 *
 * Restoring the status here keeps every status check honest: a 504 still reads as a
 * timeout (reportProgress.runFailureMessage, analytics errorClass), and a 503 that
 * cb-api itself sent (no header) stays a 503.
 */
export const UPSTREAM_STATUS_HEADER = 'x-cb-upstream-status'

const RELAY_STATUS = 503
const RELAYED = new Set([502, 504])

/** The upstream status for an HTTP status and the relay header's value (if any). */
export function upstreamStatus(status, headerValue) {
  const relayed = Number(headerValue)
  return status === RELAY_STATUS && RELAYED.has(relayed) ? relayed : status
}

// Axios hands back an AxiosHeaders (case-insensitive get) or, in tests and older
// adapters, a plain object with lower-cased names; fetch hands back a Headers.
const headerOf = (headers) => {
  if (!headers) return undefined
  if (typeof headers.get === 'function') return headers.get(UPSTREAM_STATUS_HEADER)
  return headers[UPSTREAM_STATUS_HEADER]
}

/** A fetch() Response's upstream status. */
export const responseStatus = (resp) => upstreamStatus(resp?.status, headerOf(resp?.headers))

/**
 * An axios error with the relayed status put back, in place: `response.status`,
 * `error.status`, and axios's own "Request failed with status code 503" message.
 */
export function restoreUpstreamStatus(error) {
  const res = error?.response
  if (!res) return error
  const real = upstreamStatus(res.status, headerOf(res.headers))
  if (real === res.status) return error
  res.status = real
  if (typeof error.status === 'number') error.status = real
  if (error.message === `Request failed with status code ${RELAY_STATUS}`) {
    error.message = `Request failed with status code ${real}`
  }
  return error
}
